import { APP_STORE_URL, playStoreUrl, badgeLang } from '@/game/promo';
import { sealFromPhaser } from '@/game/ui/domInput';
import { trackStoreClick } from '@/analytics';

/**
 * The official "Download on the App Store" / "Get it on Google Play" badges, as plain DOM
 * elements for Phaser's DOM layer. The menu flanks its Play button with one of each; the buy
 * card (see buyPrompt) uses the pair as a row.
 *
 * The artwork is the stores' own, shipped per language under public/images/badges/ — both Apple
 * and Google require their badge to be used unmodified, so we never re-typeset or recolour it.
 *
 * Sizing note: Apple's SVG is the badge edge-to-edge, while Google's PNG bakes in the clear space
 * their guidelines mandate (~10% on each side). Rendered at the same height the Play badge would
 * therefore look noticeably smaller, so it is drawn `PLAY_SCALE`× taller to match optically.
 */
const PLAY_SCALE = 1.25;

export type Store = 'appstore' | 'googleplay';

/**
 * Intrinsic aspect ratio of each badge file (App Store 119.66×40, Play 646×250). Both dimensions
 * are set explicitly from these so an element's width is known *before* the image loads — the
 * menu positions the badges from their width, and measuring a not-yet-loaded <img> returns 0.
 */
const SPEC: Record<Store, { aspect: number; scale: number; file: string; alt: string }> = {
  appstore: {
    aspect: 119.66 / 40, scale: 1,
    file: 'appstore-%s.svg', alt: 'Download on the App Store',
  },
  googleplay: {
    aspect: 646 / 250, scale: PLAY_SCALE,
    file: 'googleplay-%s.png', alt: 'Get it on Google Play',
  },
};

/** Rendered width in game units of `store`'s badge at a given base height. */
function badgeWidth(store: Store, height: number): number {
  const { aspect, scale } = SPEC[store];
  return Math.round(height * scale * aspect);
}

/** One badge, as a link. `height` is the App Store badge's height; Play is scaled to match. */
export function createStoreBadge(store: Store, height: number): HTMLAnchorElement {
  const { scale, file, alt } = SPEC[store];
  // Not in SPEC, which is frozen at import: the Play link carries this page's UTMs into Play's
  // acquisition report, and the page has a query string only once it is open.
  const href = store === 'appstore' ? APP_STORE_URL : playStoreUrl();
  const h = Math.round(height * scale);

  const a = document.createElement('a');
  a.className = 'store-badges__link';
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener';
  sealFromPhaser(a);   // a tap on a badge is a tap on the badge, not on the scene behind it
  // Every badge in the app funnels through here — the menu row and both cards — so this one
  // listener is the whole "they went to buy it" signal. A no-op outside the web demo, and it
  // never touches navigation: the <a> does that, tracked or not.
  a.addEventListener('click', () => trackStoreClick(store));

  const img = document.createElement('img');
  img.className = 'store-badges__img';
  img.src = `/images/badges/${file.replace('%s', badgeLang())}`;
  img.alt = alt;
  img.style.height = `${h}px`;
  img.style.width = `${badgeWidth(store, height)}px`;
  a.append(img);
  return a;
}

export interface StoreBadgesOptions {
  /** Height of the App Store badge in game units; the Play badge is scaled to match optically. */
  height: number;
  /** Gap between the two badges, in game units. */
  gap?: number;
}

/** Both badges side by side — the buy card's layout. */
export function createStoreBadges(opts: StoreBadgesOptions): HTMLElement {
  const row = document.createElement('span');
  row.className = 'store-badges';
  row.style.gap = `${opts.gap ?? 16}px`;
  sealFromPhaser(row);
  row.append(createStoreBadge('appstore', opts.height), createStoreBadge('googleplay', opts.height));
  return row;
}
